import { FormattedMessage, useIntl } from "react-intl";
import React, { useMemo, useState } from "react";
import {
  PlatformAutomationActivityLogRow,
  PlatformAutomationEnrollmentRow,
} from "../../../cores/platformAutomations/services/api";

type Props = {
  logs: PlatformAutomationActivityLogRow[];
  enrollments: PlatformAutomationEnrollmentRow[];
  filterEnrollmentId: number | null;
  onFilterChange: (enrollmentId: number | null) => void;
};

const formatDate = (value?: string | null) =>
  value ? new Date(value).toLocaleString("es-CL") : "—";

const buildSearchHaystack = (
  row: PlatformAutomationActivityLogRow,
  eventLabelText: string
) =>
  [
    row.userEmail,
    row.emailSubject,
    row.stepLabel,
    row.templateName,
    row.event,
    eventLabelText,
    row.detail,
    row.providerMessageId,
    row.enrollmentId != null ? String(row.enrollmentId) : null,
    row.enrollmentId != null ? `#${row.enrollmentId}` : null,
    row.enrollmentLabel,
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();

export default function PlatformAutomationActivityPanel({
  logs,
  enrollments,
  filterEnrollmentId,
  onFilterChange,
}: Props) {
  const intl = useIntl();
  const [searchQuery, setSearchQuery] = useState("");

  const eventLabel = (event: string) =>
    intl.formatMessage({
      id: `app.platformAutomations.activity.event.${event}`,
      defaultMessage: event,
    });

  const filteredLogs = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    if (!query) return logs;

    return logs.filter((row) =>
      buildSearchHaystack(row, eventLabel(row.event)).includes(query)
    );
  }, [logs, searchQuery, intl]);

  const filterOptions = useMemo(
    () =>
      [...enrollments].sort(
        (a, b) =>
          new Date(b.triggeredAt).getTime() - new Date(a.triggeredAt).getTime()
      ),
    [enrollments]
  );

  return (
    <div className="pa-activity">
      <div className="pa-activity__filters">
        <label className="pa-field pa-activity__filter-field">
          <span>
            <FormattedMessage id="app.platformAutomations.editor.activityFilter" />
          </span>
          <select
            value={filterEnrollmentId ?? ""}
            onChange={(e) =>
              onFilterChange(e.target.value ? Number(e.target.value) : null)
            }
          >
            <option value="">
              {intl.formatMessage({
                id: "app.platformAutomations.editor.activityFilterAll",
              })}
            </option>
            {filterOptions.map((row) => (
              <option key={row.id} value={row.id}>
                #{row.id} · {row.userEmail || "—"} ·{" "}
                {formatDate(row.triggeredAt)}
              </option>
            ))}
          </select>
        </label>

        <label className="pa-field pa-activity__search-field">
          <span>
            <FormattedMessage id="app.platformAutomations.editor.activitySearch" />
          </span>
          <input
            type="search"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder={intl.formatMessage({
              id: "app.platformAutomations.editor.activitySearchPlaceholder",
            })}
          />
        </label>

        <div className="pa-activity__filter-actions">
          {(filterEnrollmentId || searchQuery) && (
            <button
              type="button"
              className="email-mkt__ghost-btn"
              onClick={() => {
                setSearchQuery("");
                if (filterEnrollmentId) onFilterChange(null);
              }}
            >
              <FormattedMessage id="app.platformAutomations.editor.activityClearFilter" />
            </button>
          )}
          {logs.length > 0 && (
            <span className="pa-activity__count">
              <FormattedMessage
                id="app.platformAutomations.editor.activityResultsCount"
                values={{ count: filteredLogs.length, total: logs.length }}
              />
            </span>
          )}
        </div>
      </div>

      {filteredLogs.length === 0 ? (
        <div className="pa-empty-state">
          <p>
            {searchQuery.trim() ? (
              <FormattedMessage
                id="app.platformAutomations.editor.activitySearchEmpty"
                values={{ query: searchQuery.trim() }}
              />
            ) : (
              <FormattedMessage id="app.platformAutomations.editor.logsEmpty" />
            )}
          </p>
        </div>
      ) : (
        <div className="email-mkt__table pa-activity-table">
          <div className="email-mkt__table-row email-mkt__table-row--head pa-activity-table__head">
            <span>
              <FormattedMessage id="app.platformAutomations.editor.colEvent" />
            </span>
            <span>
              <FormattedMessage id="app.platformAutomations.editor.colRun" />
            </span>
            <span>
              <FormattedMessage id="app.platformAutomations.editor.colEmailStep" />
            </span>
            <span>
              <FormattedMessage id="app.platformAutomations.editor.colDetail" />
            </span>
            <span>
              <FormattedMessage id="app.platformAutomations.editor.colDate" />
            </span>
          </div>
          {filteredLogs.map((row) => {
            const emailLine =
              row.emailSubject || row.stepLabel || row.templateName || "—";

            return (
              <div className="email-mkt__table-row pa-activity-table__row" key={row.id}>
                <span>
                  <strong>{eventLabel(row.event)}</strong>
                  <small>{row.event}</small>
                </span>
                <span className="pa-activity-table__run">
                  {row.enrollmentId ? (
                    <button
                      type="button"
                      className="pa-link-btn"
                      onClick={() => onFilterChange(row.enrollmentId!)}
                    >
                      #{row.enrollmentId}
                    </button>
                  ) : (
                    "—"
                  )}
                  {row.userEmail && <small>{row.userEmail}</small>}
                </span>
                <span className="pa-activity-table__email">
                  <strong>{emailLine}</strong>
                  {row.templateName && row.emailSubject !== row.templateName && (
                    <small>{row.templateName}</small>
                  )}
                  {row.stepLabel && row.emailSubject && (
                    <small>{row.stepLabel}</small>
                  )}
                </span>
                <span className="pa-activity-table__detail">
                  {row.detail || "—"}
                  {row.providerMessageId && (
                    <small>ID: {row.providerMessageId}</small>
                  )}
                </span>
                <span>{formatDate(row.createdAt)}</span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
